import {
  addCompanyStore,
  batchCreateCompanyStores,
  deleteCompanyStore,
  getCompanyStores,
  updateCompanyStore,
} from "../actions/company.store.action";
import { CompanyStore } from "../models/company.store.model";
import { useEffect, useRef, useState } from "react";
import CompanyStoreForm from "./CompanyStoreForm";
import { FilterGroup, FilterType } from "../components/TableFilter";
import { FormRefEvent, HeadCell } from "../models/base.model";
import LoadingButton from "../components/LoadingButton";
import CompanyStoreImportForm from "./CompanyStoreImportForm";
import { useConfig } from "./ConfigContext";
import PaginatedTableList from "../components/PaginatedTableList";
import AddIcon from "@mui/icons-material/Add";

export default function CompanyStores() {
  const formRef = useRef<FormRefEvent<CompanyStore>>(null);
  const tableRef = useRef<any>(null);
  const importRef = useRef<FormRefEvent<CompanyStore[]>>(null);
  const { localized } = useConfig();

  const headCells: readonly HeadCell[] = [
    {
      id: "name",
      label: localized("Name"),
    },
    {
      id: "company",
      label: localized("Company"),
      render: (v) => <span>{v?.name}</span>,
    },
    {
      id: "storeId",
      label: localized("Store Id"),
    },
    {
      id: "storeType",
      label: localized("Store Type"),
    },
    {
      id: "area",
      label: localized("Area"),
    },
    {
      id: "region",
      label: localized("Region"),
    },
    {
      id: "address",
      label: localized("Address"),
    },
  ];

  const filters: FilterGroup<CompanyStore>[] = [
    {
      title: localized("Filter By Date"),
      items: [
        {
          name: "createdAt",
          type: FilterType.date_range,
        },
        {
          name: "updatedAt",
          type: FilterType.date_range,
        },
      ],
    },
  ];

  const onAddOrUpdate = (param?: CompanyStore) => {
    return window.webApp.setTab<CompanyStore>({
      title: `${param ? "Update" : "New"} Company Store`,
      element: <CompanyStoreForm ref={formRef} defaultValues={param} />,
      onOk: async () => {
        if (!formRef.current) {
          throw new Error("Form creation error, please refresh or contact us!");
        }
        const value = await formRef.current.onSubmit();
        const res = param
          ? await updateCompanyStore({ ...param, ...value })
          : await addCompanyStore(value);
        return res;
      },
      show: true,
    });
  };

  const onDelete = (param: CompanyStore) => {
    return deleteCompanyStore(param.id, param.companyId);
  };

  const onOpenImport = () => {
    return window.webApp.toggleDialog({
      open: true,
      title: "Import Company Stores",
      element: <CompanyStoreImportForm ref={importRef} />,
      onOk: async () => {
        if (!importRef.current) {
          throw new Error("Form creation error, please refresh or contact us!");
        }
        const stores = await importRef.current.onSubmit();

        await batchCreateCompanyStores({ stores });
        tableRef?.current.onRefresh?.();
      },
    });
  };

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          position: "absolute",
          top: -55,
        }}
      >
        <LoadingButton onClick={onOpenImport} variant="contained">
          {localized("Import by csv")}
        </LoadingButton>
      </div>
      <PaginatedTableList<CompanyStore>
        headCells={headCells}
        onAddBtnText={localized("New Company Store")}
        uid="id"
        searchKeys={["name"]}
        onAdd={onAddOrUpdate}
        ref={tableRef}
        searchText="Search by name (Case Sensitive)"
        onEdit={onAddOrUpdate}
        onDelete={onDelete}
        onLoad={getCompanyStores}
        filters={filters}
      />
    </div>
  );
}
